import type { MouseEvent } from 'react';
import type { IPartnersContact } from 'app/services/partnersApi';
import { NON_CLICKABLE_OPAQUE } from '../readOnly';

/**
 * Phase 7a — one `.row.every-row` contact line inside a company's contact pane
 * (`tmpl/partners.tmpl:222-262`).
 *
 * ★ THIS IS THE TEMPLATE ROW. A contact search replaces the whole pane with
 * `ContactSearchRow`s, which are markup-different in six ways — see that file.
 *
 * ★ DUPLICATE `id="contact_info_"` — one per contact row, 27 of them in the
 * live DOM (discovery §1 / Q12). Invalid HTML, kept verbatim: the id is
 * template-literal (note the trailing underscore, it is NOT interpolated) and
 * `render`'s dead `$('#contact_info .contact_field')` loop (Q17) only misses
 * because the id is `contact_info_`, not `contact_info`. Changing it would
 * resurrect a dead code path. No lint rule in this repo objects (there is no
 * eslint config checked in); if one is ever added it must be suppressed here
 * rather than the markup "fixed".
 *
 * ★ THE `input → i.text-clear → span.placeholder-label*` TRIPLE, again — see
 * AddressForm for why it is load-bearing. The contact labels add the
 * `.placeholder-name` / `.placeholder-email` / `.placeholder-mobile`
 * modifiers, which shift them `left:20px` and change the raised offset
 * (styles.css:250, :269, :495).
 *
 * ★ WHITESPACE DIFFERS FROM THE ADDRESS FORM ON PURPOSE. Here the template
 * puts each of `<input>`, `<i>` and `<span>` on its own line, so the parsed DOM
 * has a collapsing space text node in BOTH gaps; the address form has one only
 * between `<i>` and `<span>`. `.text-clear` is in normal flow
 * (`position:relative; display:inline-block; margin-left:-15px`), so those
 * nodes are real inline boxes — reproduced with explicit `{' '}`.
 *
 * ★ Q21 — the email input has TWO `class` attributes in the template
 * (`class="contact_field press_english"` … `class="press_english"`). HTML
 * parsing keeps the FIRST and discards the second, which is what the live DOM
 * shows (`input.contact_field.press_english`). JSX can only express one
 * `className`; the resulting class set is identical. Do not "tidy" it into
 * something that changes the class set.
 *
 * ★ The kebab is CSS-hover-REVEALED and CLICK-opened: `.dropdown_contact` is
 * `display:none` until `.every-row:hover` → `display:inline` (styles.css:360,
 * :378), and there is NO JS handler for it anywhere in `view/partners.js` —
 * unlike the company kebab, which has a `mouseover` timer. The menu itself
 * (`.open` → Bootstrap's `.open > .dropdown-menu`) is opened purely by
 * Bootstrap's dropdown data-api on CLICK (`bootstrap.js:895`), which
 * `react.html` does not ship, so in wave A this menu could never be opened at
 * all. 7b restores it: the `.open` state is owned by `ContactList` (one per
 * pane) and reproduced through `../useBootstrapDropdown`.
 *
 * ★ 7c — `change .contact_field` (§4.6, auto-PATCH + the `add:company:contact`
 * localStorage flag), `click .text-clear` (§4.4) and `click .contact_delete`
 * (§4.8) are all DELEGATED on `#ksdm-content` by `PartnersPage`
 * (`partners.js:33`, `:27`, `:31`), so this component binds none of them. The
 * `data-name="company_<co>_<ct>"` on BOTH the row and the delete anchor is what
 * `deleteContact` uses to find the row again (`:419`) — the searched row shape
 * omits it and that is Q9. Do not add it there, and do not remove it here.
 */
interface ContactRowProps {
  contact: IPartnersContact;
  /** The OWNING company's id — `{{../id}}` in the template, not the contact's. */
  companyId: number;
  /** Bootstrap's `.open` on the kebab's parent div — owned by `ContactList`. */
  open: boolean;
  onToggleMenu: (e: MouseEvent<HTMLButtonElement>, contactId: number) => void;
  /** ★ NOT A LEGACY PROP — the owning company is not this user's. See
   * `ContactList`. `readonly` + `tabindex="-1"` + `non-clickable` on the three
   * inputs, and the whole ⋮ kebab is left unrendered. */
  readOnly?: boolean;
}

const v = (value: string | null | undefined) => value ?? '';

const ContactRow = ({
  contact,
  companyId,
  open,
  onToggleMenu,
  readOnly = false,
}: ContactRowProps) => {
  /** `data-name="company_<companyId>_<contactId>"` is how `deleteContact`
   * re-finds this node (`partners.js:419`). */
  const dataName = `company_${companyId}_${contact.id}`;

  return (
    <div className="row every-row" data-id={contact.id} data-name={dataName}>
      <div className="col-xs-4 row margin-0">
        <div className="col-xs-1">
          <span className="icon-perm-identity-24px" />
        </div>
        <div className="col-xs-11 ml_35">
          <input
            autoComplete="new-password"
            className={readOnly ? 'contact_field non-clickable' : 'contact_field'}
            type="text"
            name="name"
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
            style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
            defaultValue={v(contact.name)}
            placeholder="Name"
            id="contact_info_"
          />{' '}
          <i className="text-clear icon-close-1" />{' '}
          <span className="placeholder-label placeholder-name">Name</span>
        </div>
      </div>
      <div className="col-xs-4">
        <input
          autoComplete="new-password"
          className={`contact_field press_english${readOnly ? ' non-clickable' : ''}`}
          type="email"
          name="email"
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
          defaultValue={v(contact.email)}
          placeholder="Email"
        />{' '}
        <i className="text-clear icon-close-1" />{' '}
        <span className="placeholder-label placeholder-email">Email</span>
      </div>
      <div className="col-xs-3">
        <input
          autoComplete="new-password"
          className={`contact_field phone-number-input${readOnly ? ' non-clickable' : ''}`}
          type="text"
          name="tel_mobile"
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
          defaultValue={v(contact.tel_mobile)}
          placeholder="Mobile"
        />{' '}
        <i className="text-clear icon-close-1" />{' '}
        <span className="placeholder-label placeholder-mobile">Mobile</span>
      </div>
      {/* ★ NOT LEGACY — the kebab is not rendered at all when the owning company
          is not this user's, so it cannot appear on hover. Its only item is
          Delete, which is exactly what ownership forbids. */}
      {!readOnly && (
        <div className="dropdown  dropdown_contact col-xs-1">
          <div className={open ? 'open' : ''}>
            <button
              className="btn btn-contact-delete dropdown-toggle icon-more-actions"
              type="button"
              data-toggle="dropdown"
              onClick={(e) => onToggleMenu(e, contact.id)}
            />
            <ul className="dropdown-menu">
              <li>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  tabIndex={-1}
                  href="#"
                  data-id={contact.id}
                  data-name={dataName}
                  className="remove contact_delete"
                >
                  <i className="icon-trash" />
                  Delete
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactRow;
